import { jFileLittle } from '../types/file';
import { formatImgUrl } from './images';

export const mimetypes = [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/pdf',
    'application/zip',
    'application/illustrator',
    'application/postscript',
    'application/vnd.ms-opentype',
    'application/x-font-ttf',
    'application/octet-stream',
    'audio/mp3',
    'audio/mpeg',
    'font/ttf',
    'font/otf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/vnd.adobe.photoshop',
    'image/svg+xml',
    'text/plain',
    'text/csv',
    'video/mp4',
    'video/webm',
] as const;

export type MimetypesType = (typeof mimetypes)[number];

export type fileExtensionTypes =
    | 'docx'
    | 'xlsx'
    | 'csv'
    | 'pptx'
    | 'pdf'
    | 'zip'
    | 'ai'
    | 'mp3'
    | 'mpeg'
    | 'ttf'
    | 'otf'
    | 'jpg'
    | 'jpeg'
    | 'png'
    | 'gif'
    | 'psd'
    | 'svg'
    | 'txt'
    | 'mp4'
    | 'odt'
    | 'webm';
export const extensions: fileExtensionTypes[] = [
    'docx',
    'xlsx',
    'csv',
    'pptx',
    'pdf',
    'zip',
    'ai',
    'mp3',
    'mpeg',
    'ttf',
    'otf',
    'jpg',
    'jpeg',
    'png',
    'gif',
    'psd',
    'svg',
    'txt',
    'mp4',
    'odt',
    'webm',
];

export type fileFamilyTypes = 'application' | 'gif' | 'image' | 'video' | 'audio' | 'font' | 'text' | 'folder';

export const mimetypeToExt: Record<string, fileExtensionTypes> = {
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
    'application/vnd.oasis.opendocument.text': 'odt',
    'application/pdf': 'pdf',
    'application/zip': 'zip',
    'application/x-zip-compressed': 'zip',
    'application/illustrator': 'ai',
    'application/postscript': 'ai',
    'audio/mp3': 'mp3',
    'audio/mpeg': 'mpeg',
    'font/ttf': 'ttf',
    'font/otf': 'otf',
    'image/jpeg': 'jpeg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'application/x-font-ttf': 'ttf',
    'application/vnd.ms-opentype': 'otf',
    'image/vnd.adobe.photoshop': 'psd',
    'image/svg+xml': 'svg',
    'text/plain': 'txt',
    'text/csv': 'xlsx',
    'video/mp4': 'mp4',
    'video/webm': 'mp4',
};

export const getMimetypeFromExtension = (fileExt: fileExtensionTypes) => {
    return Object.keys(mimetypeToExt).find((key) => mimetypeToExt[key] === fileExt);
};

export const getFileExtensionFromMimetype = (mimetype: string): fileExtensionTypes => {
    return mimetypeToExt[mimetype] ?? undefined;
};

export function getExtensionFromName(name: string): fileExtensionTypes {
    const tryExt = String(name).split('.').pop();

    return extensions.find((ext) => ext === tryExt) as fileExtensionTypes;
}

/**
 * This resolve bad/missing (mostly application/octet-stream) mimetype with the file name.
 * If this doesn't match any extension. Return application/octet-stream to get neutral file display
 */
export const getMimetypeFromName = (name: string) => {
    const extension = getExtensionFromName(name);

    return getMimetypeFromExtension(extension) ?? 'application/octet-stream';
};

export const objectTimestamp = (date: string | null): string | undefined => {
    return date ? (new Date(date).getTime() / 1000).toFixed(0) : undefined;
};

/**
 * Resolve file path. If undefined show file avatar
 */
export const resolveFilePath = (file: jFileLittle, size = 'fitx80') => {
    const family = resolveFileFamily(file);
    const timestamp = objectTimestamp(file.dateModified);

    switch (family) {
        default:
            return undefined;
        case 'video':
            return `/media/${file.id}.mp4?stream=true` + (timestamp ? `&_=${timestamp}` : '');
        case 'audio':
            return `/media/${file.id}.mp3` + (timestamp ? `?_=${timestamp}` : '');
        case 'gif':
            return formatImgUrl({ timestamp, size, from: 'imagecache', uri: file.uri, format: 'gif' });
        case 'image':
            return formatImgUrl({ timestamp, size, from: 'imagecache', uri: file.uri });
    }
};

export const getFileFamilyFromName = (name: string): fileFamilyTypes => {
    const ext = getExtensionFromName(name);
    if (ext !== undefined) {
        const mimetype = getMimetypeFromExtension(ext);

        return getFileFamily(mimetype);
    }

    return 'application';
};

const isNotSupported = (mimetype: string) => {
    const list = ['image/vnd', 'image/svg', 'image/heic'];
    return list.findIndex((el) => mimetype.startsWith(el)) !== -1;
};

export function getFileFamily(mimetype: string | undefined): fileFamilyTypes {
    /**
     * This is a custom mimetype to support folder display
     */
    if (mimetype && mimetype.startsWith('folder/')) {
        return 'folder';
    }
    if (mimetype === undefined || mimetype === null) {
        return 'application';
    }
    if (isNotSupported(mimetype)) {
        return 'application';
    }
    if (mimetype.startsWith('image/gif')) {
        return 'gif';
    }
    if (mimetype.startsWith('image/')) {
        return 'image';
    }
    if (mimetype.startsWith('video/')) {
        return 'video';
    }
    if (mimetype.startsWith('audio/')) {
        return 'audio';
    }
    if (
        mimetype.startsWith('font/') ||
        mimetype.startsWith('application/x-font-ttf') ||
        mimetype.startsWith('application/vnd.ms-opentype')
    ) {
        return 'font';
    }
    if (mimetype.startsWith('text/')) {
        return 'text';
    }

    return 'application';
}

export const resolveFileUrl = (idFile: number, hash: string, mode: 'ng' | 'rr' = 'rr') => {
    return mode === 'ng' ? `/ng/file/${idFile}/${hash}` : `/ng/rr/file/${idFile}/${hash}`;
};

export const resolveFileMimetype = (file: Pick<jFileLittle, 'mimetype' | 'title'>) => {
    return file.mimetype === undefined || file.mimetype === 'application/octet-stream'
        ? getMimetypeFromName(file.title)
        : file.mimetype;
};

export const resolveFileFamily = (file: jFileLittle) => {
    return file.mimetype === undefined || file.mimetype === 'application/octet-stream'
        ? getFileFamilyFromName(file.title)
        : getFileFamily(file.mimetype);
};
